import { EXAMPLE_EMPLOYEES } from './constants.js'
import { DEMO_EMPLOYEE_LIMIT } from './demoLimits.js'

export { DEMO_EMPLOYEE_LIMIT }
export const CENSUS_MODES = {
  CUSTOM: 'custom',
  EXAMPLE: 'example',
}

const deepClone = value => JSON.parse(JSON.stringify(value))

const normalizeRowForComparison = (employee) => Object.fromEntries(
  Object.entries(employee || {})
    .filter(([, value]) => value !== undefined && value !== '')
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey)),
)

const rowsEqual = (left, right) => (
  JSON.stringify(normalizeRowForComparison(left)) === JSON.stringify(normalizeRowForComparison(right))
)

export function createExampleEmployees() {
  return deepClone(EXAMPLE_EMPLOYEES)
}

export function normalizePersistedCensus(employees, censusMode, persistedBaseline) {
  const rows = Array.isArray(employees) ? employees : []
  // Prefer the baseline that was persisted alongside this census. An already
  // loaded example census keeps the sample it was loaded against, so changing
  // the bundled sample (e.g. adding allocation-group labels) only shows up when
  // the user loads example data again – and the demo edit counter stays honest.
  const exampleBaseline = Array.isArray(persistedBaseline) && persistedBaseline.length
    ? persistedBaseline
    : createExampleEmployees()

  if (censusMode === CENSUS_MODES.EXAMPLE) {
    return {
      censusMode,
      employees: rows,
      censusBaseline: exampleBaseline,
    }
  }

  return { censusMode: CENSUS_MODES.CUSTOM, employees: rows, censusBaseline: [] }
}

// Levenshtein distance at the row level: editing, adding, or deleting one row
// costs one modification against the built-in sample.
export function countBaselineModifications(employees, censusBaseline) {
  const currentRows = employees || []
  const baselineRows = censusBaseline || []
  let previous = Array.from({ length: currentRows.length + 1 }, (_, index) => index)

  for (let baselineIndex = 1; baselineIndex <= baselineRows.length; baselineIndex += 1) {
    const next = [baselineIndex]
    for (let currentIndex = 1; currentIndex <= currentRows.length; currentIndex += 1) {
      const substitutionCost = rowsEqual(
        baselineRows[baselineIndex - 1],
        currentRows[currentIndex - 1],
      ) ? 0 : 1
      next[currentIndex] = Math.min(
        previous[currentIndex] + 1,
        next[currentIndex - 1] + 1,
        previous[currentIndex - 1] + substitutionCost,
      )
    }
    previous = next
  }

  return previous[currentRows.length]
}

export function getDemoCensusUsage(censusMode, employees, censusBaseline = []) {
  if (censusMode === CENSUS_MODES.CUSTOM) return employees?.length || 0
  return countBaselineModifications(employees, censusBaseline)
}

export function isDemoCensusAllowed(censusMode, employees, censusBaseline = []) {
  return getDemoCensusUsage(censusMode, employees, censusBaseline) <= DEMO_EMPLOYEE_LIMIT
}

export function classifyDemoImport(employees) {
  if ((employees?.length || 0) <= DEMO_EMPLOYEE_LIMIT) {
    return {
      kind: 'load',
      censusMode: CENSUS_MODES.CUSTOM,
      censusBaseline: [],
    }
  }

  return { kind: 'oversized' }
}
